import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { ArrowUp, ArrowDown, BookOpen, Loader2, MessageCircle, Minus, Search, Target, TrendingUp } from "lucide-react";

export const KeywordResearchTool = ({ t, language, userEmail }) => {
  const [seedKeyword, setSeedKeyword] = useState('');
  const [country, setCountry] = useState('US');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  const countries = [
    { code: 'US', name: 'United States' }, { code: 'SK', name: 'Slovensko' },
    { code: 'CZ', name: 'Česko' }, { code: 'DE', name: 'Deutschland' },
    { code: 'PL', name: 'Polska' }, { code: 'HU', name: 'Magyarország' },
    { code: 'GB', name: 'United Kingdom' }, { code: 'FR', name: 'France' },
    { code: 'ES', name: 'España' }, { code: 'IT', name: 'Italia' },
  ];

  const intentColors = {
    informational: 'bg-[#a855f7]/10 text-[#a855f7]',
    commercial: 'bg-purple-900/30 text-purple-400',
    transactional: 'bg-green-100 text-green-700',
    navigational: 'bg-orange-100 text-orange-700',
  };

  const trendIcons = { up: <ArrowUp className="w-4 h-4 text-green-600" />, down: <ArrowDown className="w-4 h-4 text-red-500" />, stable: <Minus className="w-4 h-4 text-[var(--color-text-muted)]" /> };

  const handleSearch = async () => {
    if (!seedKeyword.trim() || isLoading) return;
    setIsLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/keyword-research`, {
        seed_keyword: seedKeyword.trim(),
        language,
        country,
        email: userEmail || ''
      });
      setResults(res.data);
    } catch (e) {
      console.error('Keyword research error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const getDifficultyColor = (d) => {
    if (d <= 30) return 'text-green-600';
    if (d <= 60) return 'text-orange-500';
    return 'text-red-600';
  };

  return (
    <div data-testid="keyword-research-tool">
      {/* Input Form */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          data-testid="keyword-input"
          value={seedKeyword}
          onChange={(e) => setSeedKeyword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder={t.keyword_placeholder}
          className="flex-1 px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none transition-colors"
        />
        <select
          data-testid="keyword-country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none bg-[var(--color-surface)]"
        >
          {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <button
          data-testid="keyword-search-btn"
          onClick={handleSearch}
          disabled={isLoading || !seedKeyword.trim()}
          className="px-6 py-3 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          {t.keyword_search}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-6 animate-fade-in" data-testid="keyword-results">
          {/* Keywords Table */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)] flex items-center justify-between">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <BookOpen className="w-5 h-5 text-[#a855f7]" />
                {t.keyword_results} ({results.total_keywords})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-bg)] border-b border-[var(--color-border)]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-[var(--color-text-muted)]">Keyword</th>
                    <th className="text-right px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.search_volume}</th>
                    <th className="text-right px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.difficulty}</th>
                    <th className="text-right px-4 py-3 font-semibold text-[var(--color-text-muted)]">CPC</th>
                    <th className="text-center px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.trend}</th>
                    <th className="text-center px-4 py-3 font-semibold text-[var(--color-text-muted)]">{t.intent}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.keywords.map((kw, i) => (
                    <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/30 transition-colors" data-testid={`keyword-row-${i}`}>
                      <td className="px-4 py-3 font-medium">{kw.keyword}</td>
                      <td className="px-4 py-3 text-right font-mono">{kw.search_volume.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${kw.difficulty <= 30 ? 'bg-green-500' : kw.difficulty <= 60 ? 'bg-orange-400' : 'bg-purple-500'}`} style={{ width: `${kw.difficulty}%` }} />
                          </div>
                          <span className={`font-mono ${getDifficultyColor(kw.difficulty)}`}>{kw.difficulty}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">${kw.cpc.toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">{trendIcons[kw.trend] || trendIcons.stable}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${intentColors[kw.intent] || 'bg-[var(--color-surface-alt)] text-[var(--color-text-muted)]'}`}>
                          {kw.intent}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Related Topics, Questions, Long-tail */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="related-topics">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm font-['Chivo']">{t.related_topics}</h4>
              </div>
              <div className="p-4 space-y-2">
                {results.related_topics.map((topic, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Target className="w-3.5 h-3.5 text-[#a855f7] flex-shrink-0" />
                    <span>{topic}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="questions-list">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm font-['Chivo']">{t.questions_asked}</h4>
              </div>
              <div className="p-4 space-y-2">
                {results.questions.map((q, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <MessageCircle className="w-3.5 h-3.5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span>{q}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="long-tail-list">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm font-['Chivo']">{t.long_tail_keywords}</h4>
              </div>
              <div className="p-4 space-y-2">
                {results.long_tail.map((lt, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <TrendingUp className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <span>{lt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
